/* eslint-disable max-lines */
import type { ViewRecordFieldChange } from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  getVisibleNodeNames,
  type ModelSearcher,
  type ViewQueryList,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { stableStringify } from '@salescore/features'
import { logger } from '@salescore/frontend-common'
import { App } from 'antd'
import { Array, pipe, Result } from 'effect'
import { isUndefined } from 'effect/Predicate'
import { t } from 'i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import z from 'zod'

import type { RSheetRecordNode } from '../domain/column'
import {
  applyMagnifyingGlassChange,
  type MagnifyingGlassChange,
} from '../domain/useUpsertViewRecordsEntry/applyMagnifyingGlassChange'
import { buildMagnifyingGlassRecordsImplUpdates } from '../domain/useUpsertViewRecordsEntry/buildMagnifyingGlassRecordsImplUpdates'
import { hasChangesWithoutEditPermission } from '../domain/useUpsertViewRecordsEntry/hasChangesWithoutEditPermission'
import { selectAllowedNewRecordChanges } from '../domain/useUpsertViewRecordsEntry/selectAllowedNewRecordChanges'
import { recordIdsAtom, type UpdateRecordsImplArgument, useRecordsImpl, useUpdateRecordsImpl } from '../recoil/records'
import type { FeedbackMessage } from '../recoil/records/atoms'
import { type RelationChange, updateRecordNodeRelations } from '../recoil/records/mutations/updateRecordNodeRelations'
import { useEvaluateFieldCallbacks } from '../recoil/records/mutations/useEvaluateFieldCallbacks'
import { changesAtom } from '../recoil/recordsHistory'
import { type UpdateRowConfigsImplArgument, useUpdateRowConfigsImpl } from '../recoil/rowConfig'
import type { SalescoreObjectAbility, ViewAbility } from '../recoil/view/atoms'
import { useCanBeCreatedWithoutAbility } from '../recoil/view/hooks/canBeCreatedWithoutAbility'
import { useModelSearcher } from '../recoil/view/selectors/modelSearcherSelector'
import { addNewRecords, type NewRecordChange } from '../rsheet/action/addNewRecord/addNewRecords'
import { type DeepReadonly, toMutable } from '../rsheet/types/DeepReadonly'
import { type RecordChange, updateViewRecords } from '../rsheet/util/updateViewRecords'

interface Argument {
  newRecordChanges?: DeepReadonly<NewRecordChange[]> // 新規レコード追加の変更情報
  recordChanges: DeepReadonly<RecordChange[]> // 既存レコード更新の変更情報
  magnifyingGlassChanges?: MagnifyingGlassChange[] // 虫眼鏡セルへ変更を加えた場合の変更情報
  relationChanges?: DeepReadonly<RelationChange[]> // 虫眼鏡セルへ変更を加えた場合以外の参照の変更情報
  recordsBeforeHeightAdjustment?: RSheetRecordNode[] // レコードの高さを変更する前のレコードノード。 change の snapshot に使うために必要。
  recordsAfterHeightAdjustment?: RSheetRecordNode[] // レコードの高さを変更した後のレコードノード。ペースト時に高さを変更してから upsertViewRecords を呼び出すために必要。
}

export interface UpsertViewRecordsReturnType {
  changes: ViewRecordFieldChange[]
}

type RecordIdsMutation = (current: string[]) => string[]

// 最後にまとめて Atom に反映するための変更を蓄積するオブジェクト
interface MutationAccumulator {
  feedbackMessages: FeedbackMessage[]
  updateRecordsImplArgument: UpdateRecordsImplArgument
  updateRowConfigsImplArgument: UpdateRowConfigsImplArgument
  recordIdsMutations: RecordIdsMutation[]
  changes: ViewRecordFieldChange[]
}

/**
 * addNewRecords を呼び出して、その結果を MutationAccumulator に反映する
 * accumulator 以外の引数はすべて DeepReadonly に変更することで、副作用を防ぐ
 */
const handleAddNewRecords = ({
  records,
  recordIds,
  newRecordChanges,
  accumulator,
  visibleNodeNames,
}: {
  records: DeepReadonly<ViewQueryRecordNode[]>
  recordIds: DeepReadonly<string[]>
  newRecordChanges: DeepReadonly<NewRecordChange[]> | undefined
  accumulator: MutationAccumulator
  visibleNodeNames: ReturnType<typeof getVisibleNodeNames>
}): {
  addNewRecordsResult: ReturnType<typeof addNewRecords>
} => {
  const addNewRecordsResult = addNewRecords(records, recordIds, newRecordChanges ?? [])
  pipe(
    addNewRecordsResult.results,
    Array.forEach((result) => {
      pipe(
        result,
        Result.match({
          onFailure: (left) => {
            if (isUndefined(left)) {
              return
            }
            accumulator.feedbackMessages.push({
              message: t(left.message),
              type: 'warn' as const,
            })
          },
          onSuccess: (right) => {
            if (right.change !== undefined) {
              accumulator.changes.push(right.change)
            }
            if (right.kind === 'root') {
              accumulator.updateRecordsImplArgument.push({
                id: right.recordNode.id,
                value: toMutable(assignInnerRowGeometry(right.recordNode, visibleNodeNames)),
              })
            } else {
              accumulator.updateRecordsImplArgument.push({
                id: right.rootRecordNode.id,
                value: toMutable(assignInnerRowGeometry(right.rootRecordNode, visibleNodeNames)),
              })
              accumulator.updateRowConfigsImplArgument.push({
                id: right.rootRecordNode.id,
                value: { expanded: true },
              })
            }
          },
        }),
      )
    }),
  )

  if (addNewRecordsResult.recordIds.length !== recordIds.length) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- pure 移行が完了したら as は外す
    accumulator.recordIdsMutations.push(() => addNewRecordsResult.recordIds as string[])
  }

  return {
    addNewRecordsResult,
  }
}

/**
 * upsertViewRecords を呼び出して、その結果を MutationAccumulator に反映する
 * accumulator 以外の引数はすべて DeepReadonly に変更することで、副作用を防ぐ
 */
const handleUpdateViewRecords = ({
  records,
  recordChanges,
  view,
  modelSearcher,
  accumulator,
  visibleNodeNames,
}: {
  records: DeepReadonly<ViewQueryRecordNode[]>
  recordChanges: DeepReadonly<RecordChange[]>
  view: DeepReadonly<ViewQueryList>
  modelSearcher: ModelSearcher
  accumulator: MutationAccumulator
  visibleNodeNames: ReturnType<typeof getVisibleNodeNames>
}): {
  updateResults: ReturnType<typeof updateViewRecords>
} => {
  const updateResults = updateViewRecords({
    initialRecords: records,
    view,
    recordChanges,
    modelSearcher,
  })

  pipe(
    updateResults.results,
    Array.forEach((result) => {
      pipe(
        result,
        Result.match({
          onFailure: (left) => {
            if (left === undefined) {
              return
            }
            const parsedLeft = z
              .object({ message: z.string(), arguments: z.record(z.string(), z.string()) })
              .safeParse(left)
            const feedbackMessage = parsedLeft.success
              ? {
                  message: t(parsedLeft.data.message, parsedLeft.data.arguments),
                  type: 'warn' as const,
                }
              : {
                  message: t(left.message),
                  type: 'warn' as const,
                }
            accumulator.feedbackMessages.push(feedbackMessage)
          },
          onSuccess: (right) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- 仕方ない
            accumulator.changes.push(...(right.changes as ViewRecordFieldChange[]))
            if (right.recordNode.id === undefined) {
              return
            }
            switch (right.rootChangeType) {
              case 'delete': {
                accumulator.updateRecordsImplArgument.push({ id: right.recordNode.id, value: undefined })
                accumulator.recordIdsMutations.push((ids) => ids.filter((id) => id !== right.recordNode.id))
                break
              }
              case 'update':
              case 'none': {
                accumulator.updateRecordsImplArgument.push({
                  id: right.recordNode.id,
                  value: assignInnerRowGeometry(toMutable(right.recordNode), visibleNodeNames),
                })
                break
              }
            }
          },
        }),
      )
    }),
  )

  return {
    updateResults,
  }
}

/**
 * 虫眼鏡セルへの変更は、親ノードに参照カラムがない場合を考慮する必要がある点が特殊
 * applyMagnifyingGlassChange を呼び出して、その結果を MutationAccumulator に反映する
 * records を DeepReadonly にして副作用を防いでいる
 */
const handleMagnifyingGlassChanges = ({
  records,
  magnifyingGlassChanges,
  accumulator,
  query,
  visibleNodeNames,
}: {
  records: DeepReadonly<ViewQueryRecordNode[]>
  magnifyingGlassChanges: MagnifyingGlassChange[] | undefined
  accumulator: MutationAccumulator
  query: DeepReadonly<ViewQueryList>
  visibleNodeNames: ReturnType<typeof getVisibleNodeNames>
}) => {
  const result = Array.reduce<
    MagnifyingGlassChange,
    {
      allRootNodes: DeepReadonly<ViewQueryRecordNode[]>
      changes: ViewRecordFieldChange[]
      referencingNodes: ViewQueryRecordNode[]
      referencedNodes: ViewQueryRecordNode[]
      feedbackMessages: FeedbackMessage[]
    }
  >(
    magnifyingGlassChanges ?? [],
    {
      allRootNodes: records,
      changes: [],
      referencingNodes: [],
      referencedNodes: [],
      feedbackMessages: [],
    },
    (accumulator_, magnifyingGlassChange) => {
      const applyEither = applyMagnifyingGlassChange(accumulator_.allRootNodes, query, magnifyingGlassChange)
      if (Result.isFailure(applyEither)) {
        if (isUndefined(applyEither.failure)) {
          return accumulator_
        }
        return {
          ...accumulator_,
          feedbackMessages: [
            ...accumulator_.feedbackMessages,
            {
              message: t(applyEither.failure.message),
              type: 'warn' as const,
            },
          ],
        }
      }
      const result = applyEither.success
      if (result === 'referenceAlreadyUptoDate') {
        return accumulator_
      }
      const { records, change: changeHistory, referencingNode, referencedNode } = result
      return {
        ...accumulator_,
        allRootNodes: records,
        changes: [...accumulator_.changes, ...changeHistory.changes],
        referencingNodes: [...accumulator_.referencingNodes, referencingNode],
        referencedNodes: [...accumulator_.referencedNodes, referencedNode],
      }
    },
  )
  accumulator.changes.push(...result.changes)
  accumulator.updateRecordsImplArgument.push(
    ...buildMagnifyingGlassRecordsImplUpdates(
      [...result.referencedNodes, ...result.referencingNodes],
      visibleNodeNames,
    ),
  )
  accumulator.feedbackMessages.push(...result.feedbackMessages)
  return result
}

/**
 * updateRecordNodeRelations を呼び出して、その結果を MutationAccumulator に反映する
 * accumulator 以外の引数はすべて DeepReadonly に変更することで、副作用を防ぐ
 */
const handleRelationChanges = ({
  records,
  relationChanges,
  accumulator,
  query,
}: {
  records: DeepReadonly<ViewQueryRecordNode[]>
  relationChanges: DeepReadonly<RelationChange[]> | undefined
  accumulator: MutationAccumulator
  query: DeepReadonly<ViewQueryList>
}): ReturnType<typeof updateRecordNodeRelations> => {
  const updateRecordNodeRelationsResult = updateRecordNodeRelations(records, relationChanges ?? [], query)
  for (const recordNode of updateRecordNodeRelationsResult.updatedRootNodes) {
    if (recordNode.id === undefined) {
      continue
    }
    accumulator.updateRecordsImplArgument.push({
      id: recordNode.id,
      value: toMutable(recordNode),
    })
  }
  return updateRecordNodeRelationsResult
}

/**
 * レコードの追加、更新、関連レコードの更新を一括で行う
 * アトムに反映するための変更を MutationAccumulator に蓄積して返す
 */
export const handleChanges = ({
  records,
  recordIds,
  newRecordChanges,
  recordChanges,
  relationChanges,
  magnifyingGlassChanges,
  query,
  modelSearcher,
}: {
  records: ViewQueryRecordNode[]
  recordIds: string[]
  newRecordChanges: DeepReadonly<NewRecordChange[]> | undefined
  recordChanges: DeepReadonly<RecordChange[]>
  relationChanges: DeepReadonly<RelationChange[]> | undefined
  magnifyingGlassChanges: MagnifyingGlassChange[] | undefined
  query: DeepReadonly<ViewQueryList>
  modelSearcher: ModelSearcher
}): { mutations: MutationAccumulator; finalRecords: DeepReadonly<ViewQueryRecordNode[]> } => {
  const visibleNodeNames = getVisibleNodeNames(query)
  const mutations: MutationAccumulator = {
    feedbackMessages: [],
    updateRecordsImplArgument: [],
    updateRowConfigsImplArgument: [],
    recordIdsMutations: [],
    changes: [],
  }

  const { addNewRecordsResult } = handleAddNewRecords({
    records,
    recordIds,
    newRecordChanges,
    accumulator: mutations,
    visibleNodeNames,
  })

  const { allRootNodes: relationResults } = handleRelationChanges({
    records: addNewRecordsResult.rootNodes,
    relationChanges,
    accumulator: mutations,
    query,
  })

  const { allRootNodes: magnifyingGlassResults } = handleMagnifyingGlassChanges({
    records: relationResults,
    magnifyingGlassChanges,
    accumulator: mutations,
    query,
    visibleNodeNames,
  })

  const { updateResults } = handleUpdateViewRecords({
    records: magnifyingGlassResults,
    recordChanges,
    view: query,
    modelSearcher,
    accumulator: mutations,
    visibleNodeNames,
  })

  return {
    mutations,
    // 戻り値は履歴のスナップショットとフィールドコールバックの再帰入力になる。
    // アトムへ反映する木は正規化して渡しているため、ここを通さないと両者で値が揃わない。
    finalRecords: toMutable(updateResults.records).map((record) => assignInnerRowGeometry(record, visibleNodeNames)),
  }
}

// レコードの追加、更新、関連レコードの更新を一括で行う handleChanges を呼び出すためのフック
export const useUpsertViewRecordsEntry = (
  query: ViewQueryList | undefined,
  ability: ViewAbility & SalescoreObjectAbility,
): ((argument: Argument) => UpsertViewRecordsReturnType) => {
  const records = useRecordsImpl()
  const recordIds = useRecoilValue(recordIdsAtom)
  const modelSearcher = useModelSearcher()

  const updateRecordsImpl = useUpdateRecordsImpl()
  const updateRowConfigsImpl = useUpdateRowConfigsImpl()
  const { message } = App.useApp()
  const setRecordIds = useSetRecoilState(recordIdsAtom)
  const setChanges = useSetRecoilState(changesAtom)
  const canBeCreatedWithoutAbility = useCanBeCreatedWithoutAbility()
  const evaluateFieldCallbacks = useEvaluateFieldCallbacks(query)

  const apply = ({
    recordChanges,
    relationChanges,
    magnifyingGlassChanges,
    newRecordChanges,
    recordsBeforeHeightAdjustment = records,
    recordsAfterHeightAdjustment = records,
  }: Argument): UpsertViewRecordsReturnType => {
    if (!ability.canSaveRecord && !ability.canCreateSalescoreObjectRecord && !ability.canEditSalescoreObjectRecord) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
      void message.warning(t(`このシートのレコードを編集する権限がありません`))
      return { changes: [] }
    }
    if (query === undefined) {
      // query が undefined なのは kpi/kpiPivot view 経由で本 hook が mount された場合のみ。
      // kpi/kpiPivot の column 型では編集系の呼び出し (apply) に到達しないため空結果で安全。
      return { changes: [] }
    }

    if (
      hasChangesWithoutEditPermission({
        canEditModelRecord: ability.canEditModelRecord,
        newRecordChanges: newRecordChanges ?? [],
        recordChanges,
        magnifyingGlassChanges: magnifyingGlassChanges ?? [],
        canBeCreatedWithoutAbility,
      })
    ) {
      // 権限のない列を含む操作は、一部だけ反映せずに操作ごと止める。
      // 一部だけ反映すると、参照項目の付け替えを捨てて参照先の値だけが付け替え前のレコードに入るなど、つながった変更の片方だけが残るため。
      // フィールドのコールバックが作る変更もここを通るため、権限のないオブジェクトへのコールバックの変更は止まり、先に反映したユーザーの変更だけが残る。
      // コールバックを設定したシートに限られ、警告が出るため受け入れる (!7744)
      void message.warning(t(`このシートのレコードを編集する権限がありません`))
      return { changes: [] }
    }

    const { allowedNewRecordChanges, allowedRecordChanges, hasDeniedNewRecordChanges } = selectAllowedNewRecordChanges({
      ability,
      newRecordChanges: newRecordChanges ?? [],
      recordChanges,
      canBeCreatedWithoutAbility,
    })
    if (hasDeniedNewRecordChanges) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
      void message.warning(t(`権限がないため、新規レコード作成はできません`))
    }

    const { mutations, finalRecords } = handleChanges({
      records: recordsAfterHeightAdjustment,
      recordIds,
      newRecordChanges: allowedNewRecordChanges,
      recordChanges: allowedRecordChanges,
      magnifyingGlassChanges,
      relationChanges,
      query,
      modelSearcher,
    })

    // Atom には一回でまとめて反映する
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
    mutations.feedbackMessages.forEach((feedbackMessage) => {
      void message.warning(feedbackMessage.message)
    })

    if (mutations.recordIdsMutations.length > 0) {
      const finalRecordIds = mutations.recordIdsMutations.reduce((currentIds, mutate) => mutate(currentIds), recordIds)
      setRecordIds(finalRecordIds)
    }

    // AtomFamily の変更については、レコード ID ごとに最後の値を反映すれば良い
    pipe(
      mutations.updateRecordsImplArgument,
      Array.reverse,
      Array.dedupeWith((a, b) => a.id === b.id),
      updateRecordsImpl,
    )
    pipe(
      mutations.updateRowConfigsImplArgument,
      Array.reverse,
      Array.dedupeWith((a, b) => a.id === b.id),
      updateRowConfigsImpl,
    )

    if (mutations.changes.length > 0) {
      const newChange = {
        datetime: new Date().toISOString(),
        // eslint-disable-next-line sonarjs/todo-tag
        rowIndices: [0], // TODO: 結局使ってないので廃止
        snapshot: JSON.stringify(recordsBeforeHeightAdjustment), // snapshot には高さ更新前のレコードを入れる
        snapshotAfterChange: JSON.stringify(finalRecords),
        changes: mutations.changes,
      }
      setChanges((oldChanges) => [...oldChanges, newChange])

      // カラムにコールバックが設定されている場合呼び出す
      void evaluateFieldCallbacks(newChange)
        .then((recordChanges) => {
          // 再帰的に呼び出す
          const mutableFinalRecords = toMutable(finalRecords)
          return apply({
            recordChanges,
            recordsBeforeHeightAdjustment: mutableFinalRecords,
            recordsAfterHeightAdjustment: mutableFinalRecords,
          })
        })
        .catch(() => {
          logger.warn(
            'SHEET_FIELD_CALLBACK',
            `フィールドに設定されたコールバックの呼び出し中に失敗しました。\nchanges=${stableStringify(newChange.changes)}`,
          )
        })
    }

    return {
      changes: mutations.changes,
    }
  }
  return apply
}
